import { Instance, Styling, SearchFallback, Feedback } from './types';
import config from '@config';
import state from '@state';
import { overrideSearchForms } from '@form';
import { observeDynamicContent } from '@utils/dom';
import { log } from '@utils/logger';
import { applyCustomStyling } from '@utils/styling'; // Import the styling function
import i18n, { initI18n } from '@i18n';

/**
 * Options for initializing the AgentEx library.
 */
interface InitOptions {
    instances?: Instance[]; // Custom selectors for targeting DOM elements
    styling?: Styling; // Custom styling configuration
    webhookUrl: string; // Webhook URL for sending search queries
    pollingUrl?: string; // Optional polling URL for checking query status
    agentId: string; // API key for authentication
    wafEnabled?: boolean; // Enable or disable WAF (Web Application Firewall)
    debug?: boolean; // Enable or disable debug mode
    loadingText?: string; // Custom loading text
    searchFallback?: SearchFallback; // Fallback configuration for search
    feedback?: Feedback; // Configuration for Feedback
    searchPage?: string // A body class where agent ex won't be initialised on
    persistResults?: string // Adds an option to persist results between pages on the website, or at source only
    showSources?: boolean; // Show sources for results
}

/**
 * Initializes the AgentEx library with the provided options.
 *
 * @param options - Configuration options for initialization
 */
export function init(options: InitOptions): void {
    (async () => {
        await initI18n();

        if (state.initialized) {
            log('Already initialized');
            return;
        }

        if (document.body.getAttribute('data-aex-no-forms') === 'true' || document.body.getAttribute('data-aex-search-page') === 'true') {
            return;
        }

        if (options.instances) {
            const invalidInstance = options.instances.find(instance => !instance.mode);

            if (invalidInstance) {
                console.error(i18n.t('error.initError', { prefix: i18n.t('log.prefix') }));
                return;
            }

            config.instances = options.instances;
        } else {
            console.error(i18n.t('error.instanceError', { prefix: i18n.t('log.prefix') }));
            return;
        }

        if (options.styling) {
            config.styling = options.styling;
            applyCustomStyling(options.styling); // Apply custom styling
        }

        if (!options.agentId) {
            console.error(i18n.t('error.agentIdError', { prefix: i18n.t('log.prefix') }));
            return;
        }

        if (options.loadingText) {
            config.loadingText = options.loadingText;
        }

        config.agentId = options.agentId;
        config.webhookUrl = config.webhookUrl.replace('{agentId}', options.agentId);
        config.wafEnabled = options?.wafEnabled || false;
        config.debug = options.debug ?? config.debug;
        config.SearchFallback = options.searchFallback ?? config.SearchFallback;
        config.feedback = options.feedback ?? config.feedback;
        config.persistResults = options.persistResults ?? undefined;
        config.showSources = options.showSources ?? false;

        if (options.searchFallback && options.searchFallback.enabled) {
            if (!options.searchFallback.searchQueryParamName || !options.searchFallback.searchURLPath) {
                console.error(i18n.t('error.searchInitError', { prefix: i18n.t('log.prefix') }));
                return;
            }
        }

        if (options.feedback && options.feedback.enabled) {
            if (!options.feedback.feedbackLinkPath || !options.feedback.feedbackLinkText) {
                console.error(i18n.t('error.searchFeedbackError', { prefix: i18n.t('log.prefix') }));
                return;
            }
        }

        if (options.searchPage && document.body.classList.contains(options.searchPage)) {
            document.body.setAttribute('data-aex-search-page', 'true');
            return;
        }

        overrideSearchForms();
        observeDynamicContent();

        state.initialized = true;
        state.isActive = true;
        document.body.classList.add('aex-active');
        log(i18n.t('log.success'));
    })();
}
